'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchRealtyOfferTypes } from '@eliadmin/core/src/api/realty/realty-api';
import type {
  CategoriesEntity,
  GeneralLayout,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { useQuery } from '@tanstack/react-query';
import { cloneElement, isValidElement, type CSSProperties, type ReactNode } from 'react';

import {
  ArrowRightIcon,
  ChevronDownIcon,
  CloseIcon,
  EmailIcon,
  MenuIcon,
  PhoneIcon,
  RealtySocialIcon,
  RealtyTypeIcon,
} from './realty-header-icons';
import { RealtyHeaderLogo } from './realty-header-logo';
import { buildRealtyHeaderIntentHref, REALTY_HEADER_OFFER_TYPE_IDS } from './realty-header-recipe';
import styles from './realty-header.module.css';

type RealtyType = {
  id: number;
  name: string;
};

type MenuItem = {
  type: 'page';
  name: string;
  path?: string;
};

export type RealtyMobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: MenuItem[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  realtyTypes?: RealtyType[];
  sellHref?: string;
  style?: CSSProperties;
};

function resolveNavigationHref(value?: string | null) {
  const href = value?.trim();
  if (!href || href.startsWith('#')) return null;

  if (href.startsWith('/') && !href.startsWith('//')) return getSafeHref(href);

  if (href.startsWith('//')) {
    try {
      const parsed = new URL(`https:${href}`);
      return parsed.protocol === 'https:' ? parsed.toString() : null;
    } catch {
      return null;
    }
  }

  if (/^www\./i.test(href)) {
    try {
      return new URL(`https://${href}`).toString();
    } catch {
      return null;
    }
  }

  if (/^https?:\/\//i.test(href)) {
    try {
      const parsed = new URL(href);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : null;
    } catch {
      return null;
    }
  }

  if (/^[a-z][a-z\d+.-]*:/i.test(href)) return null;

  return getSafeHref(href);
}

function getPhoneHref(value?: string | null) {
  const displayValue = value?.trim();
  if (!displayValue) return null;

  const phone = displayValue.replace(/[^+\d,;*]/g, '');
  return phone.replace(/\D/g, '').length >= 3 ? `tel:${phone}` : null;
}

function getEmailHref(value?: string | null) {
  const email = value?.trim();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return null;
  return `mailto:${email}`;
}

function getSocialHref(value: unknown) {
  return typeof value === 'string' ? resolveNavigationHref(value) : null;
}

function hasSafeSocialLinks(socialMedia: GeneralLayout['data']['social_media']) {
  if (!socialMedia) return false;

  return Object.entries(socialMedia).some(([key, value]) => {
    const icon = HEADER_CONFIG.socialMediaIcons[key as keyof GeneralLayout['data']['social_media']];
    const hasExactIcon = ['facebook', 'instagram', 'linkedin'].includes(key);
    return Boolean(getSocialHref(value)) && (hasExactIcon || isValidElement(icon));
  });
}

function ClosableNavigationLink({
  href,
  className,
  dataIntent,
  dataPart,
  children,
}: {
  href: string;
  className?: string;
  dataIntent?: string;
  dataPart?: string;
  children: ReactNode;
}) {
  if (isRelativeHref(href)) {
    return (
      <SheetClose asChild>
        <Link
          href={href}
          prefetch={false}
          className={className}
          data-intent={dataIntent}
          data-eli-part={dataPart}
        >
          {children}
        </Link>
      </SheetClose>
    );
  }

  return (
    <SheetClose asChild>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        data-intent={dataIntent}
        data-eli-part={dataPart}
      >
        {children}
      </a>
    </SheetClose>
  );
}

function SafeSocialLinks({
  socialMedia,
  className,
}: {
  socialMedia: GeneralLayout['data']['social_media'];
  className?: RealtyMobileSidebarProps['className'];
}) {
  if (!socialMedia) return null;

  const links = Object.entries(socialMedia).flatMap(([key, value]) => {
    const href = getSocialHref(value);
    const icon = HEADER_CONFIG.socialMediaIcons[key as keyof GeneralLayout['data']['social_media']];
    const hasExactIcon = ['facebook', 'instagram', 'linkedin'].includes(key);

    if (!href || (!hasExactIcon && !isValidElement<{ className?: string }>(icon))) return [];

    return [
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={key}
        className={cn(styles.socialButton, styles.socialLink)}
      >
        {hasExactIcon ? (
          <RealtySocialIcon platform={key} className={cn(styles.socialIcon, className?.icon)} />
        ) : isValidElement<{ className?: string }>(icon) ? (
          cloneElement(icon, {
            className: cn(styles.socialIcon, className?.icon),
          })
        ) : null}
      </a>,
    ];
  });

  if (!links.length) return null;

  return (
    <div
      className={cn(styles.socialWrapper, className?.iconsWrapper)}
      data-eli-part="drawer-social-block"
    >
      {links}
    </div>
  );
}

export function RealtyMobileSidebar(props: RealtyMobileSidebarProps) {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const locale = useLocaleStore(state => state.currentLocale);
  const { t } = useTranslations();

  const { data: offerTypeResponse, isPending: areOfferTypesPending } = useQuery({
    queryKey: [CacheKey.REALTY_OFFER_TYPES, locale],
    queryFn: () => fetchRealtyOfferTypes({ locale }),
    enabled: Boolean(locale),
    staleTime: 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const availableOfferTypeIds = new Set(
    Array.isArray(offerTypeResponse?.data)
      ? offerTypeResponse.data
          .map(option => option?.id)
          .filter((id): id is number => Number.isFinite(id) && id > 0)
      : [],
  );

  const categorySource = props.realtyTypes?.length
    ? props.realtyTypes
    : (props.categories?.map(category => ({ id: Number(category.id), name: category.name })) ?? []);
  const realtyTypes = categorySource.filter(
    type =>
      Number.isFinite(type?.id) &&
      type.id > 0 &&
      typeof type.name === 'string' &&
      Boolean(type.name.trim()),
  );
  const menu = (props.menu ?? []).filter(
    item => typeof item?.name === 'string' && Boolean(item.name.trim()),
  );

  const phone = generalLayout.contact?.phone?.trim();
  const email = generalLayout.contact?.email?.trim();
  const phoneHref = getPhoneHref(phone);
  const emailHref = getEmailHref(email);
  const hasContactLinks = Boolean(phoneHref || emailHref);
  const hasSocialLinks = hasSafeSocialLinks(generalLayout.social_media);
  const hasDrawerSections = Boolean(
    realtyTypes.length || menu.length || hasContactLinks || hasSocialLinks,
  );
  const sellHref =
    resolveNavigationHref(props.sellHref) ?? resolveNavigationHref(ROUTES.CONTACT()) ?? '/';

  return (
    <Sheet>
      <SheetTrigger
        className={cn(styles.mobileTrigger, props.className?.trigger)}
        aria-label={t('text3241')}
        data-eli-part="mobile-trigger"
      >
        <MenuIcon className={styles.mobileIcon} />
        <span>{t('text3241')}</span>
      </SheetTrigger>

      <SheetContent
        side={props.side ?? 'right'}
        className={cn(styles.drawer, props.className?.content)}
        style={props.style}
        showCloseIcon={false}
        aria-describedby={undefined}
        data-eli-part="mobile-drawer"
      >
        <SheetTitle className="sr-only">{t('text3241')}</SheetTitle>

        <div className={styles.drawerTop} data-eli-part="mobile-drawer-top">
          <RealtyHeaderLogo generalLayout={generalLayout} />
          <SheetClose className={styles.mobileClose} aria-label={t('text1855')}>
            <CloseIcon className={styles.mobileIcon} />
            <span>{t('text1855')}</span>
          </SheetClose>
        </div>

        <div className={styles.drawerBody} data-eli-part="mobile-drawer-body">
          <div className={cn('w-full', props.className?.buttonWrapper)}>
            <ClosableNavigationLink
              href={sellHref}
              className={cn(styles.mobileCta, props.className?.button)}
              dataPart="drawer-sell-cta"
            >
              {t('text1395')}
            </ClosableNavigationLink>
          </div>

          {hasDrawerSections ? (
            <div
              className={cn(styles.divider, props.className?.firstSeparator)}
              data-eli-part="drawer-divider-categories"
            />
          ) : null}

          {realtyTypes.length ? (
            <Accordion
              type="single"
              collapsible
              className={cn(styles.drawerCategories, props.className?.menuList)}
              data-eli-part="drawer-category-block"
            >
              {realtyTypes.map((type, index) => {
                const intents = [
                  {
                    key: 'sell',
                    label: t('text1395'),
                    href: sellHref,
                  },
                  {
                    key: 'buy',
                    label: t('text1396'),
                    href: availableOfferTypeIds.has(REALTY_HEADER_OFFER_TYPE_IDS.buy)
                      ? buildRealtyHeaderIntentHref(
                          resolveNavigationHref(ROUTES.REALTIES()) ?? '/',
                          type.id,
                          REALTY_HEADER_OFFER_TYPE_IDS.buy,
                        )
                      : null,
                  },
                  {
                    key: 'rent',
                    label: t('text1397'),
                    href: availableOfferTypeIds.has(REALTY_HEADER_OFFER_TYPE_IDS.rent)
                      ? buildRealtyHeaderIntentHref(
                          resolveNavigationHref(ROUTES.REALTIES()) ?? '/',
                          type.id,
                          REALTY_HEADER_OFFER_TYPE_IDS.rent,
                        )
                      : null,
                  },
                ] as const;

                return (
                  <AccordionItem
                    key={`${type.id}-${index}`}
                    value={`realty-type-${type.id}-${index}`}
                    className={styles.accordionItem}
                  >
                    <AccordionTrigger
                      hideIcon
                      className={styles.accordionTrigger}
                      data-realty-type-id={type.id}
                    >
                      <span className={styles.accordionLabel}>
                        <RealtyTypeIcon
                          typeId={type.id}
                          className={styles.categoryIcon}
                          data-eli-part="category-icon"
                        />
                        <span>{type.name.trim()}</span>
                      </span>
                      <ChevronDownIcon
                        className={styles.accordionChevron}
                        data-eli-part="category-chevron"
                      />
                    </AccordionTrigger>
                    <AccordionContent className={styles.accordionContent}>
                      {intents.map(intent => {
                        const content = (
                          <>
                            <span>{intent.label}</span>
                            <ArrowRightIcon className={styles.actionArrow} />
                          </>
                        );

                        return intent.href ? (
                          <ClosableNavigationLink
                            key={`${type.id}-${intent.key}`}
                            href={intent.href}
                            className={styles.drawerAction}
                            dataIntent={intent.key}
                          >
                            {content}
                          </ClosableNavigationLink>
                        ) : (
                          <span
                            key={`${type.id}-${intent.key}`}
                            className={styles.drawerAction}
                            role="link"
                            aria-disabled="true"
                            aria-busy={areOfferTypesPending || undefined}
                            data-intent={intent.key}
                            data-state="disabled"
                          >
                            {content}
                          </span>
                        );
                      })}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          ) : null}

          {realtyTypes.length &&
          (menu.length || hasContactLinks || hasSocialLinks) &&
          props.menuSeparator !== false ? (
            <div
              className={cn(styles.divider, props.className?.menuSeparator)}
              data-eli-part="drawer-divider-nav"
            />
          ) : null}

          {menu.length ? (
            <nav
              className={cn(styles.drawerNav, props.className?.menuGroup)}
              aria-label={t('text3241')}
              data-eli-part="drawer-nav-block"
              data-figma-menu-size={
                menu.length === 4 && menu.every(item => item.name.trim().length <= 32)
                  ? 'exact'
                  : 'fluid'
              }
            >
              {menu.map((item, index) => {
                const href = resolveNavigationHref(item.path);
                const content = (
                  <>
                    <span>{item.name.trim()}</span>
                    {props.menuArrow ? <ArrowRightIcon className={styles.actionArrow} /> : null}
                  </>
                );

                return href ? (
                  <ClosableNavigationLink
                    key={`${item.name}-${item.path ?? index}`}
                    href={href}
                    className={cn(styles.drawerNavLink, props.className?.menuLink)}
                  >
                    {content}
                  </ClosableNavigationLink>
                ) : (
                  <span
                    key={`${item.name}-${index}`}
                    className={cn(styles.drawerNavLink, props.className?.menuLink)}
                  >
                    {content}
                  </span>
                );
              })}
            </nav>
          ) : null}

          {menu.length && (hasContactLinks || hasSocialLinks) && props.menuSeparator !== false ? (
            <div
              className={cn(styles.divider, props.className?.menuSeparator)}
              data-eli-part="drawer-divider-contact"
            />
          ) : null}

          {hasContactLinks ? (
            <div className={styles.drawerContact} data-eli-part="drawer-contact-block">
              {phoneHref && phone ? (
                <SheetClose asChild>
                  <a href={phoneHref} className={styles.contactLink}>
                    <PhoneIcon className={styles.mobileIcon} />
                    <span>{phone}</span>
                  </a>
                </SheetClose>
              ) : null}
              {emailHref && email ? (
                <SheetClose asChild>
                  <a href={emailHref} className={styles.contactLink}>
                    <EmailIcon className={styles.mobileIcon} />
                    <span>{email}</span>
                  </a>
                </SheetClose>
              ) : null}
            </div>
          ) : null}

          {hasContactLinks && hasSocialLinks && props.menuSeparator !== false ? (
            <div
              className={cn(styles.divider, props.className?.menuSeparator)}
              data-eli-part="drawer-divider-social"
            />
          ) : null}

          {hasSocialLinks ? (
            <SafeSocialLinks socialMedia={generalLayout.social_media} className={props.className} />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export type MobileSidebarProps = RealtyMobileSidebarProps;

export default RealtyMobileSidebar;
